import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao, type FuncionarioFormacao } from '@/context/FuncionariosFormacaoContext';
import { estadoNormalizado } from '@/utils/estadoUtils';
import Pagination from '@/components/base/Pagination';

const CARREIRA_CFG: Record<string, { bg: string; text: string }> = {
  'Técnica Superior':       { bg: 'bg-zinc-100',   text: 'text-zinc-600'    },
  'Assistente Técnico':     { bg: 'bg-amber-50',   text: 'text-amber-700'   },
  'Assistente Operacional': { bg: 'bg-emerald-50', text: 'text-emerald-700' },
};

const AVATAR_PALETTE = [
  { bg: 'bg-rose-100',    text: 'text-rose-600'    },
  { bg: 'bg-amber-100',   text: 'text-amber-600'   },
  { bg: 'bg-teal-100',    text: 'text-teal-600'    },
  { bg: 'bg-cyan-100',    text: 'text-cyan-600'    },
  { bg: 'bg-orange-100',  text: 'text-orange-600'  },
  { bg: 'bg-emerald-100', text: 'text-emerald-600' },
];

function getAvatar(id: string) {
  const n = parseInt(id.replace(/[^0-9]/g, '').slice(0, 6) || '0', 10);
  return AVATAR_PALETTE[n % AVATAR_PALETTE.length];
}

function getInitials(nome: string) {
  return nome
    .split(' ')
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((n) => n[0] ?? '')
    .join('')
    .toUpperCase();
}

interface ColaboradorStats {
  funcionario: FuncionarioFormacao;
  totalHoras: number;
  totalAcoes: number;
  concluidas: number;
  certificados: number;
  taxaCert: number;
  ultimaData: string | null;
  ultimaFormacao: string | null;
  horasPorArea: Record<string, number>;
  diasDesdeUltima: number | null;
}

type Ordenacao = 'nome' | 'horas' | 'acoes' | 'concluidas' | 'certificados' | 'taxa_cert' | 'ultima';

export default function ColaboradoresFormacaoPage() {
  const navigate = useNavigate();
  const { formacoes, participantes, loading } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();

  const [search, setSearch] = useState('');
  const [carrFilter, setCarrFilter] = useState('');
  const [unidFilter, setUnidFilter] = useState('');
  const [ordenacao, setOrdenacao] = useState<Ordenacao>('horas');
  const [ordemDesc, setOrdemDesc] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Calcular estatísticas por colaborador
  const statsPorColaborador = useMemo<ColaboradorStats[]>(() => {
    return funcionarios.map((func) => {
      const parts = participantes.filter((p) => p.funcionario_id === func.id);
      const formIds = new Set(parts.map((p) => p.formacao_id));
      const formacoesColab = formacoes.filter((f) => formIds.has(f.id));

      const totalHoras = formacoesColab.reduce((s, f) => s + (f.duracao_horas ?? 0), 0);
      const concluidas = formacoesColab.filter((f) => estadoNormalizado(f.estado) === 'concluida').length;
      const certificados = parts.filter((p) => p.certificado_emitido).length;
      const taxaCert = formacoesColab.length > 0 ? Math.round((certificados / formacoesColab.length) * 100) : 0;

      let ultimaData: string | null = null;
      let ultimaFormacao: string | null = null;
      formacoesColab.forEach((f) => {
        const d = f.data_fim ?? f.data_inicio ?? null;
        if (d && (!ultimaData || d > ultimaData)) {
          ultimaData = d;
          ultimaFormacao = f.nome;
        }
      });

      const diasDesdeUltima = ultimaData
        ? Math.floor((Date.now() - new Date(ultimaData).getTime()) / (1000 * 60 * 60 * 24))
        : null;

      const horasPorArea: Record<string, number> = {};
      formacoesColab.forEach((f) => {
        const cat = f.categoria ?? 'Outros';
        horasPorArea[cat] = (horasPorArea[cat] ?? 0) + (f.duracao_horas ?? 0);
      });

      return {
        funcionario: func,
        totalHoras,
        totalAcoes: formacoesColab.length,
        concluidas,
        certificados,
        taxaCert,
        ultimaData,
        ultimaFormacao,
        horasPorArea,
        diasDesdeUltima,
      };
    });
  }, [funcionarios, formacoes, participantes]);

  // Filtros
  const filtered = useMemo(() => {
    return statsPorColaborador.filter((c) => {
      const matchSearch =
        search === '' ||
        c.funcionario.nome.toLowerCase().includes(search.toLowerCase()) ||
        (c.funcionario.email ?? '').toLowerCase().includes(search.toLowerCase());
      const matchCarr = carrFilter === '' || c.funcionario.carreira === carrFilter;
      const matchUnid = unidFilter === '' || c.funcionario.unidadeOrganica === unidFilter;
      return matchSearch && matchCarr && matchUnid;
    }).sort((a, b) => {
      let diff = 0;
      switch (ordenacao) {
        case 'nome':      diff = a.funcionario.nome.localeCompare(b.funcionario.nome); break;
        case 'horas':     diff = a.totalHoras - b.totalHoras; break;
        case 'acoes':     diff = a.totalAcoes - b.totalAcoes; break;
        case 'concluidas':diff = a.concluidas - b.concluidas; break;
        case 'certificados': diff = a.certificados - b.certificados; break;
        case 'taxa_cert': diff = a.taxaCert - b.taxaCert; break;
        case 'ultima':
          diff = (a.ultimaData ?? '0').localeCompare(b.ultimaData ?? '0');
          break;
        default: diff = 0;
      }
      return ordemDesc ? -diff : diff;
    });
  }, [statsPorColaborador, search, carrFilter, unidFilter, ordenacao, ordemDesc]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  // KPIs globais
  const kpis = useMemo(() => {
    const totalHoras = statsPorColaborador.reduce((s, c) => s + c.totalHoras, 0);
    const totalCert = statsPorColaborador.reduce((s, c) => s + c.certificados, 0);
    const totalAcoes = statsPorColaborador.reduce((s, c) => s + c.totalAcoes, 0);
    const totalConcluidas = statsPorColaborador.reduce((s, c) => s + c.concluidas, 0);
    const semFormacao = statsPorColaborador.filter((c) => c.totalAcoes === 0).length;
    const comCert100 = statsPorColaborador.filter((c) => c.taxaCert === 100 && c.totalAcoes > 0).length;
    return { totalHoras, totalCert, totalAcoes, totalConcluidas, semFormacao, comCert100 };
  }, [statsPorColaborador]);

  const unidades = useMemo(() => {
    const set = new Set(funcionarios.map((f) => f.unidadeOrganica).filter(Boolean));
    return Array.from(set).sort();
  }, [funcionarios]);

  function toggleSort(col: Ordenacao) {
    if (ordenacao === col) {
      setOrdemDesc((d) => !d);
    } else {
      setOrdenacao(col);
      setOrdemDesc(true);
    }
  }

  function exportCsv() {
    const headers = ['Nome', 'Carreira', 'Unidade', 'Formações', 'Concluídas', 'Horas', 'Certificados', 'Taxa Cert.', 'Última Formação', 'Data Última'];
    const rows = filtered.map((c) => [
      c.funcionario.nome,
      c.funcionario.carreira ?? '',
      c.funcionario.unidadeOrganica ?? '',
      String(c.totalAcoes),
      String(c.concluidas),
      String(c.totalHoras),
      String(c.certificados),
      `${c.taxaCert}%`,
      c.ultimaFormacao ?? '',
      c.ultimaData ? new Date(c.ultimaData).toLocaleDateString('pt-PT') : '',
    ]);
    const csv = [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `colaboradores-formacao-${new Date().getFullYear()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) {
    return (
      <main className="flex-1 bg-slate-50/60 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" />
          </div>
          <p className="text-[13px] text-zinc-400">A carregar colaboradores...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 bg-slate-50/60 overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-zinc-100 px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
              <i className="ri-user-star-line text-white text-lg" />
            </div>
            <div>
              <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação</span>
              <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Colaboradores</h1>
              <p className="text-[12px] text-zinc-400 mt-0.5">Estatísticas de formação por funcionário</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={exportCsv}
              className="whitespace-nowrap flex items-center gap-2 text-[13px] font-medium text-zinc-600 border border-zinc-200 bg-white px-4 py-2.5 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-download-line text-sm" /></div>
              Exportar CSV
            </button>
            <button
              onClick={() => navigate('/formacao/funcionarios')}
              className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-team-line text-sm" /></div>
              Gerir Funcionários
            </button>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-5">
        {/* KPIs globais */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          {[
            { label: 'Colaboradores',     value: funcionarios.length,      icon: 'ri-team-line',          bg: 'bg-zinc-100 text-zinc-600' },
            { label: 'Horas Totais',      value: `${kpis.totalHoras}h`,    icon: 'ri-time-line',          bg: 'bg-zinc-100 text-zinc-600' },
            { label: 'Formações',         value: kpis.totalAcoes,          icon: 'ri-book-open-line',      bg: 'bg-zinc-100 text-zinc-600' },
            { label: 'Concluídas',        value: kpis.totalConcluidas,     icon: 'ri-checkbox-circle-line', bg: 'bg-emerald-50 text-emerald-600' },
            { label: 'Certificados',      value: kpis.totalCert,           icon: 'ri-medal-line',         bg: 'bg-emerald-50 text-emerald-600' },
            { label: 'Sem Formação',      value: kpis.semFormacao,         icon: 'ri-user-unfollow-line', bg: 'bg-amber-50 text-amber-600' },
          ].map((k) => (
            <div key={k.label} className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-col gap-1.5">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-zinc-100">
                <i className={`${k.icon} text-zinc-500 text-sm`} />
              </div>
              <p className="text-[22px] font-bold text-zinc-900 leading-none">{k.value}</p>
              <p className="text-[11px] text-zinc-400 font-medium leading-tight">{k.label}</p>
            </div>
          ))}
        </div>

        {/* Top performers mini */}
        {kpis.comCert100 > 0 && (
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-5 py-3 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100">
              <i className="ri-medal-line text-emerald-600 text-sm" />
            </div>
            <p className="text-[13px] font-bold text-emerald-800">
              {kpis.comCert100} colaborador(es) com 100% de taxa de certificação
            </p>
            <button
              onClick={() => { setOrdenacao('taxa_cert'); setOrdemDesc(true); setSearch(''); }}
              className="whitespace-nowrap ml-auto text-[12px] font-semibold text-emerald-700 hover:text-emerald-900 cursor-pointer"
            >
              Ver ranking →
            </button>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-wrap gap-2.5 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
              <i className="ri-search-line text-sm" />
            </div>
            <input
              type="text"
              placeholder="Pesquisar colaborador..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
            />
          </div>
          <select
            value={carrFilter}
            onChange={(e) => setCarrFilter(e.target.value)}
            className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer"
          >
            <option value="">Todas as carreiras</option>
            <option value="Técnica Superior">Técnica Superior</option>
            <option value="Assistente Técnico">Assistente Técnico</option>
            <option value="Assistente Operacional">Assistente Operacional</option>
          </select>
          {unidades.length > 0 && (
            <select
              value={unidFilter}
              onChange={(e) => setUnidFilter(e.target.value)}
              className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer max-w-[220px]"
            >
              <option value="">Todas as unidades</option>
              {unidades.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          )}
          <span className="text-[12px] text-zinc-400 ml-auto">
            <span className="font-semibold text-zinc-600">{filtered.length}</span> resultado(s)
          </span>
        </div>

        {/* Tabela */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-user-search-line text-4xl text-zinc-200" />
            </div>
            <p className="text-[14px] font-semibold text-zinc-400">Nenhum colaborador encontrado</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50">
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide cursor-pointer select-none" onClick={() => toggleSort('nome')}>
                      <div className="flex items-center gap-1">Funcionário {ordenacao === 'nome' && <i className={`${ordemDesc ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'} text-xs`} />}</div>
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Carreira</th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide hidden lg:table-cell">Unidade</th>
                    <th className="px-4 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide cursor-pointer select-none" onClick={() => toggleSort('acoes')}>
                      <div className="flex items-center gap-1 justify-center">Formações {ordenacao === 'acoes' && <i className={`${ordemDesc ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'} text-xs`} />}</div>
                    </th>
                    <th className="px-4 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide cursor-pointer select-none" onClick={() => toggleSort('concluidas')}>
                      <div className="flex items-center gap-1 justify-center">Concluídas {ordenacao === 'concluidas' && <i className={`${ordemDesc ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'} text-xs`} />}</div>
                    </th>
                    <th className="px-4 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide cursor-pointer select-none" onClick={() => toggleSort('horas')}>
                      <div className="flex items-center gap-1 justify-center">Horas {ordenacao === 'horas' && <i className={`${ordemDesc ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'} text-xs`} />}</div>
                    </th>
                    <th className="px-4 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide cursor-pointer select-none" onClick={() => toggleSort('certificados')}>
                      <div className="flex items-center gap-1 justify-center">Certs {ordenacao === 'certificados' && <i className={`${ordemDesc ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'} text-xs`} />}</div>
                    </th>
                    <th className="px-4 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide cursor-pointer select-none" onClick={() => toggleSort('taxa_cert')}>
                      <div className="flex items-center gap-1 justify-center">Taxa {ordenacao === 'taxa_cert' && <i className={`${ordemDesc ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'} text-xs`} />}</div>
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide hidden xl:table-cell cursor-pointer select-none" onClick={() => toggleSort('ultima')}>
                      <div className="flex items-center gap-1">Última {ordenacao === 'ultima' && <i className={`${ordemDesc ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'} text-xs`} />}</div>
                    </th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-50">
                  {paginated.map((c) => {
                    const palette = getAvatar(c.funcionario.id);
                    const cc = CARREIRA_CFG[c.funcionario.carreira] ?? CARREIRA_CFG['Técnica Superior'];
                    const areaTop = Object.entries(c.horasPorArea).sort((a, b) => b[1] - a[1])[0];

                    return (
                      <tr
                        key={c.funcionario.id}
                        className="hover:bg-zinc-50/60 transition-colors cursor-pointer border-b border-zinc-50"
                        onClick={() => navigate(`/formacao/funcionarios/${c.funcionario.id}`)}
                      >
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 flex items-center justify-center rounded-full text-[11px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
                              {getInitials(c.funcionario.nome)}
                            </div>
                            <div className="min-w-0">
                              <p className="text-[13px] font-semibold text-zinc-800 truncate">{c.funcionario.nome}</p>
                              {areaTop && (
                                <p className="text-[10px] text-zinc-400 truncate">{areaTop[0]} · {areaTop[1]}h</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-full ${cc.bg} ${cc.text}`}>
                            {c.funcionario.carreira}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 hidden lg:table-cell">
                          <p className="text-[12px] text-zinc-500 truncate max-w-[160px]">{c.funcionario.unidadeOrganica ?? '—'}</p>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className="text-[13px] font-bold text-zinc-700">{c.totalAcoes}</span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className={`text-[13px] font-bold ${c.concluidas > 0 ? 'text-emerald-600' : 'text-zinc-300'}`}>{c.concluidas}</span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className="text-[13px] font-bold text-zinc-700 tabular-nums">{c.totalHoras}h</span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {c.certificados > 0 ? (
                              <>
                                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-50">
                                  <i className="ri-medal-line text-emerald-500 text-xs" />
                                </div>
                                <span className="text-[13px] font-bold text-emerald-600">{c.certificados}</span>
                              </>
                            ) : (
                              <span className="text-[13px] font-bold text-zinc-300">—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-12 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${c.taxaCert}%`,
                                  backgroundColor: c.taxaCert >= 80 ? '#10b981' : c.taxaCert >= 50 ? '#f59e0b' : '#94a3b8',
                                }}
                              />
                            </div>
                            <span className={`text-[11px] font-bold ${c.taxaCert >= 80 ? 'text-emerald-600' : c.taxaCert >= 50 ? 'text-amber-600' : 'text-zinc-400'}`}>
                              {c.taxaCert}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 hidden xl:table-cell">
                          {c.ultimaData ? (
                            <div>
                              <p className="text-[11px] font-medium text-zinc-600 truncate max-w-[140px]">{c.ultimaFormacao ?? '—'}</p>
                              <p className="text-[10px] text-zinc-400">
                                {new Date(c.ultimaData).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}
                                {c.diasDesdeUltima !== null && c.diasDesdeUltima > 365 && (
                                  <span className="ml-1 text-amber-600 font-semibold">· +{Math.floor(c.diasDesdeUltima / 30)}m</span>
                                )}
                              </p>
                            </div>
                          ) : (
                            <span className="text-[12px] font-semibold text-zinc-300">Sem registo</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={() => navigate(`/formacao/funcionarios/${c.funcionario.id}`)}
                              className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer"
                              title="Ver detalhe"
                            >
                              <i className="ri-eye-line text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {filtered.length > 0 && (
          <Pagination
            currentPage={safePage}
            totalItems={filtered.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
            itemLabel="colaborador(es)"
          />
        )}
      </div>
    </main>
  );
}